import { FileDashedIcon } from "@phosphor-icons/react/dist/csr/FileDashed";
import { Link } from "react-router-dom";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { documentIcon } from "@/profitability/document-chip";
import { DOCUMENT_STATUS } from "@/profitability/document-constants";
import { MetaList } from "@/profitability/meta-list";
import {
  type DocumentEntry,
  documentRows,
  rowsLabel,
} from "./document-entries";

/** How a document's read stands, when it is not ready. */
export const readStatus = (status: string) =>
  status === DOCUMENT_STATUS.pending
    ? "Reading…"
    : status === DOCUMENT_STATUS.failed
      ? "Couldn't read"
      : null;

/**
 * One line of the list: the file, what it is for and how many rows it holds.
 * An input with no file says "Not uploaded" and links to the upload.
 */
export const DocumentListRow = ({
  entry,
  selected,
  onSelect,
  uploadHref,
  showPurpose,
}: {
  entry: DocumentEntry;
  selected: boolean;
  onSelect: (documentId: string) => void;
  uploadHref: string | null;
  showPurpose: boolean;
}) => {
  const { document, slot } = entry;
  if (!document)
    return (
      <Stack direction="row" gap={2.5} align="start" className="px-2 py-2">
        <FileDashedIcon
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-subtlest"
        />
        <Stack gap={0} className="min-w-0 flex-1">
          <Text variant="label" tone="muted" truncate>
            {slot?.title}
          </Text>
          <Text variant="caption" tone="muted">
            <MetaList>
              Not uploaded
              {uploadHref && (
                <Link
                  to={uploadHref}
                  className="text-link underline-offset-2 hover:underline"
                >
                  Upload
                </Link>
              )}
            </MetaList>
          </Text>
        </Stack>
      </Stack>
    );
  const Icon = documentIcon(document.file_name);
  const status = readStatus(document.status);
  return (
    <button
      type="button"
      aria-current={selected ? "true" : undefined}
      onClick={() => onSelect(document.id)}
      className={cn(
        "flex w-full min-w-0 items-start gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-neutral-subtle-hovered focus-visible:outline-2 focus-visible:outline-focus",
        { "bg-selected hover:bg-selected-hovered": selected },
      )}
    >
      <Icon
        aria-hidden
        className={cn("mt-0.5 size-4 shrink-0 text-subtle", {
          "text-ink": selected,
        })}
      />
      <Stack gap={0} className="min-w-0 flex-1">
        <Text variant="label" truncate title={document.file_name}>
          {document.file_name}
        </Text>
        <Text variant="caption" tone="muted">
          <MetaList>
            {showPurpose && slot ? slot.title : null}
            {status ??
              (document.kind === "text"
                ? "Text"
                : rowsLabel(documentRows(document)))}
          </MetaList>
        </Text>
      </Stack>
    </button>
  );
};
